import { useRef, useState, type ChangeEvent } from 'react';
import { Avatar } from '../../components/Avatar';
import { Icon } from '../../components/Icon';
import { useAnnouncer } from '../../components/Status';
import type { PersonWithNames } from '../../domain/types';
import { useI18n } from '../../i18n';
import { preparePortrait } from '../../lib/image';
import { uuid } from '../../lib/uuid';
import { useApp } from '../../app/AppState';
import { useRepos } from '../../repositories';
import { CameraDialog } from '../tree/CameraDialog';

/**
 * The photo shown in a person's circle: take one with the camera or choose one
 * from the device. Used on the tree's person card (owner, helper, or anyone for
 * an unclaimed circle) and on Edit details (where administrators change it too).
 */
/** Shared upload flow for the picker button and the gallery's "+" tile. */
export function usePortraitUpload(person: PersonWithNames) {
  const { t } = useI18n();
  const repos = useRepos();
  const { reload } = useApp();
  const { toast } = useAnnouncer();
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [camera, setCamera] = useState(false);
  const pickRef = useRef<HTMLInputElement | null>(null);
  const captureRef = useRef<HTMLInputElement | null>(null);

  const save = async (file: Blob) => {
    setBusy(true);
    try {
      const prepared = await preparePortrait(file);
      const res = await repos.people.addPortrait(person.id, prepared.blob, { width: prepared.width, height: prepared.height }, uuid());
      if (res.ok) {
        toast(t('portrait.saved'));
        reload();
      } else {
        toast(t(('messageKey' in res ? res.messageKey : 'error.unexpected') as Parameters<typeof t>[0]));
      }
    } catch {
      toast(t('portrait.failed'));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void save(file);
  };

  /**
   * Prefer the in-app camera (webcam on a PC, front camera on a phone) whenever
   * the browser can stream it; fall back to the native camera picker only where
   * it cannot. Deciding by "touch screen" was wrong — touch laptops and handheld
   * PCs ended up in the file dialog.
   */
  const takePhoto = () => {
    setMenu(false);
    const canStream = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;
    if (canStream) setCamera(true);
    else if (captureRef.current) captureRef.current.click();
  };
  const choosePhoto = () => {
    setMenu(false);
    pickRef.current?.click();
  };

  const elements = (
    <>
      <input ref={pickRef} type="file" accept="image/*" hidden onChange={onFile} />
      <input ref={captureRef} type="file" accept="image/*" capture="user" hidden onChange={onFile} />
      {menu && (
        <div className="photo-menu" role="menu">
          <button type="button" role="menuitem" className="btn btn-secondary" onClick={takePhoto}>
            <Icon name="camera" />
            <span>{t('portrait.take')}</span>
          </button>
          <button type="button" role="menuitem" className="btn btn-secondary" onClick={choosePhoto}>
            <Icon name="image" />
            <span>{t('portrait.choose')}</span>
          </button>
        </div>
      )}
      <CameraDialog
        open={camera}
        onClose={() => setCamera(false)}
        onCapture={(blob) => {
          setCamera(false);
          void save(blob);
        }}
      />
    </>
  );
  /*
   * `takePhoto` and `choosePhoto` are handed back as well as the little inline
   * menu, because the camera on a person's constellation is a 34px disc on the
   * rim of a circle in the middle of a full-screen overlay: a menu anchored to
   * it would hang off the edge of the picture. It asks the two questions in a
   * sheet of its own instead, and reuses everything below it — the hidden
   * inputs, the webcam dialog, the prepare-and-save.
   */
  return { busy, elements, open: () => setMenu((v) => !v), menuOpen: menu, takePhoto, choosePhoto, save };
}

/**
 * `adminOnly` — true when the viewer may change this photo *only* because Admin
 * mode is on. The camera then takes the admin colour, because changing someone
 * else's face is a power, and every power in this app is purple.
 */
export function PortraitPicker({ person, allowed, adminOnly = false }: { person: PersonWithNames; allowed: boolean; adminOnly?: boolean }) {
  const { t, lang } = useI18n();
  const { busy, elements, open, menuOpen } = usePortraitUpload(person);
  const hasPhoto = !!person.portraitUrl;
  const label = adminOnly ? t('portrait.adminChange') : hasPhoto ? t('portrait.change') : t('portrait.add');
  return (
    <span className="portrait-box">
      <Avatar person={person} lang={lang} size="lg" />
      {allowed && (
        <>
          <button type="button" className={`portrait-btn ${adminOnly ? 'is-admin-control' : ''}`} onClick={open} disabled={busy} aria-expanded={menuOpen} aria-label={label} title={label}>
            <Icon name="camera" />
          </button>
          {elements}
        </>
      )}
    </span>
  );
}

